import { observer } from 'mobx-react-lite'
import { useMemo } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import TextField from '@mui/material/TextField'
import { themeConfig } from '@gap/shared/theme'
import FileUploadWithProgress from 'reusable/mui/file-uploads/FileUploadWithProgress'
import { getFileUploadProps } from '@gap/utils/file/file-utils'
import GapDialog from 'reusable/gap-dialog/GapDialog'
import DocEmailAttachmentsTabs from 'features/gap-components/doc-page/panels/utility-panel/email/attachments/DocEmailAttachmentsTabs'
import ProposalAttachment from 'features/proposals/view/ProposalAttachment'
import { useProposalsEditReviewStore } from './ProposalsEditReviewStore'

export default observer(function ProposalsEditReviewAttachments() {
   const proposalsEditReviewStore = useProposalsEditReviewStore()

   const Attachments = useMemo(() => {
      return proposalsEditReviewStore.attachments.map((attachment, index) => (
         <ProposalAttachment
            key={`${attachment.fileName || attachment.link}-${index}`}
            attachment={attachment}
            proposal={proposalsEditReviewStore.proposal}
            canDelete={true}
            onDelete={proposalsEditReviewStore.removeAttachment}
         />
      ))
   }, [proposalsEditReviewStore.attachments, proposalsEditReviewStore.proposal, proposalsEditReviewStore.removeAttachment])

   return <Box sx={{ mt: 2, width: '100%' }}>
      <Box display="flex">
         <Box sx={{ flexGrow: 8 }}>
            <Typography variant="body1" fontWeight="700">Share links or files</Typography>
            <Typography variant="body2" sx={{ color: themeConfig.primary[600], mt: 0.5 }}>
               Any files or links you share here will be sent with the proposal and visible to everyone who can view it.
            </Typography>
         </Box>
         <Box>
            <Button variant="outlined" size="small" onClick={proposalsEditReviewStore.addAttachmentModal.open}>
               Add
            </Button>
         </Box>
      </Box>

      <AddAttachmentModal />

      {proposalsEditReviewStore.attachments.length > 0 && <Box sx={{ mt: 3 }}>
         {Attachments}
      </Box>}
   </Box>
})

const AddAttachmentModal = observer(function AddAttachmentModal() {
   const proposalsEditReviewStore = useProposalsEditReviewStore()

   return <GapDialog
      size="medium"
      title="Share links or files"
      modalState={proposalsEditReviewStore.addAttachmentModal}
      cancel={true}
      primary={proposalsEditReviewStore.attachmentModalPrimaryButton}
   >
      {proposalsEditReviewStore.attachmentForm.tabValue.bindInput((ip) => (
         <DocEmailAttachmentsTabs options={proposalsEditReviewStore.attachmentTabOptions} {...ip} />
      ))}

      <Divider sx={{ mt: 4 }} />

      <Box sx={{ mt: 4 }}>
         {proposalsEditReviewStore.attachmentFields.tabValue === 'fromYourDevice' && <FileTab />}
         {proposalsEditReviewStore.attachmentFields.tabValue === 'shareALink' && <LinkTab />}
      </Box>
   </GapDialog>
})

const FileTab = observer(function FileTab() {
   const proposalsEditReviewStore = useProposalsEditReviewStore()
   return <>
      <Typography variant="body1" sx={{ fontWeight: 700, mb: 4 }}>Add files from your device</Typography>
      <FileUploadWithProgress
         files={[]}
         getPresignedUrl={proposalsEditReviewStore.getPresignedUrl}
         onFilesValidated={proposalsEditReviewStore.setFile}
         onUploadStarted={proposalsEditReviewStore.onUploadStarted}
         onLastFileRemoved={proposalsEditReviewStore.releaseUploadGate}
         formatFile
         maxFiles={500}
         {...getFileUploadProps('proposalAttachment')}
      />
   </>
})

const LinkTab = observer(function LinkTab() {
   const proposalsEditReviewStore = useProposalsEditReviewStore()
   return <Box>
      <Typography variant="body1" sx={{ fontWeight: 700 }}>Share a link</Typography>
      <Typography variant="caption" sx={{ mt: 0.5, color: themeConfig.primary[600] }}>
         Paste a URL to a public web address, such as to Xero, Box, Loom, or other content stored online.
      </Typography>

      {proposalsEditReviewStore.attachmentForm.linkName.bindInput((ip) => (
         <TextField autoFocus fullWidth label="Title*" {...ip} sx={{ mt: 4 }} />
      ))}

      {proposalsEditReviewStore.attachmentForm.link.bindInput((ip) => (
         <TextField fullWidth label="URL*" {...ip} sx={{ mt: 2 }} />
      ))}
   </Box>
})
